// @LTG-ID: contact-submit-client
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: ADR-032 §3 (KGOB dual-write payload shape),
//        /app/backend/app/routes/contact.py (POST contract),
//        CHG-0113 (utm capture wire-up)
// @CHANGED: 2026-07-07 session current (CHG-0113 · attribution.utm sub-doc)
// @OWNER: web
//
// CHG-0083-M-A2 — Submit client for POST /api/contact.
//
// Builds the payload matching the EXISTING backend Pydantic contract
// (`ContactSubmissionRequest` in /app/backend/app/routes/contact.py
// lines 75-82): `{ name, email, message, source?, attribution? }`.
//
// Per ORCH-0087 point 2 + ADR-032 §3: the extra v4 fields (firstName,
// middleName, lastName, businessName, state, phone, category,
// email_verified_at, kgob_idempotency_key) are folded into the
// `attribution` dict — NOT into top-level fields. CHG-0084's
// `kgob_mapper.py` will translate these into KGOB's split-name schema
// downstream; this turn the LTG backend stores them verbatim under
// `attribution` and they remain readable for both sides of the dual-write.
//
// CHG-0113: marketing attribution (utm_*, click-ids, referrer, landing_page,
// session_id, first_touch_at) is folded under `attribution.utm` (nested
// sub-doc, distinct from the KGOB personal fields at `attribution.<X>`).
// Populated from sessionStorage via getAttributionForSubmit() — first-hop-
// wins semantics per ADR-009 preserve original campaign source across
// SPA navigation.

import { getAttributionForSubmit } from "../../lib/url-utils";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export class SubmitError extends Error {
    constructor(kind, msg, opts) {
        super(msg);
        this.name = "SubmitError";
        // kind ∈ "rate_limit" | "unavailable" | "network" | "validation"
        this.kind = kind;
        this.retryAfter = (opts && opts.retryAfter) || null;
        this.validationDetails = (opts && opts.validationDetails) || null;
    }
}

function buildPayload(formData, idempotencyKey) {
    const fullName = [
        formData.firstName,
        formData.middleName,
        formData.lastName,
    ]
        .filter((s) => s && s.trim())
        .join(" ")
        .trim();
    return {
        name: fullName,
        email: (formData.email || "").trim(),
        message: (formData.message || "").trim(),
        source: "contact-form",
        // CHG-0112 A2 · Honeypot field. Sent as top-level so backend can
        // check + short-circuit before touching Mongo. Empty on legitimate
        // submissions; any non-empty value triggers a silent 202 response.
        honeypot: (formData.website || "").trim(),
        attribution: {
            firstName: formData.firstName,
            middleName: formData.middleName || null,
            lastName: formData.lastName,
            businessName: formData.businessName,
            state: formData.state,
            phone: formData.phone || null,
            category: formData.category,
            email_verified_at: formData.email_verified_at || null,
            kgob_idempotency_key: idempotencyKey,
            // CHG-0113 · Marketing attribution sub-doc.
            //
            // Nested under `attribution.utm` so it stays out of the KGOB
            // personal-fields namespace (firstName, businessName, phone, ...)
            // that CHG-0084's kgob_mapper.py operates on. Contents flow from
            // sessionStorage first-touch snapshot:
            //   - utm_source / utm_medium / utm_campaign / utm_term / utm_content
            //   - gclid, fbclid, li_fat_id, msclkid  (paid-channel click ids)
            //   - referrer, landing_page, session_id, first_touch_at
            //
            // Empty object is normal for direct-traffic visits with no
            // campaign params + no prior sessionStorage — backend accepts
            // `attribution: Optional[dict]` and persists verbatim.
            utm: getAttributionForSubmit(),
        },
    };
}

export async function submitContact(formData, idempotencyKey) {
    const payload = buildPayload(formData, idempotencyKey);
    let resp;
    try {
        resp = await fetch(`${BACKEND}/api/contact`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Request-ID": idempotencyKey,
            },
            body: JSON.stringify(payload),
        });
    } catch (e) {
        throw new SubmitError("network", "Network error: " + (e.message || e));
    }
    if (resp.status === 201) {
        const data = await resp.json();
        return data; // { id, received_at, email_send_status }
    }
    // CHG-0112 A2 · 202 Accepted = honeypot silent-success. Backend
    // returned no-op response to avoid tipping off the spam bot. From the
    // client perspective this is a "success" — Confirmation renders,
    // localStorage draft clears, URL params push — but no record was
    // persisted server-side. This is intentional; a legitimate user who
    // ever hits this path (e.g. accidentally focused hidden field via
    // dev tools) will see the confirmation UI and can retry.
    if (resp.status === 202) {
        // Return a stable shape so MultiStepForm's Confirmation branch
        // still receives the fields it reads. `id` is null because
        // nothing was persisted.
        let data = null;
        try {
            data = await resp.json();
        } catch (e) {
            /* silent — 202 body is optional */
        }
        return data || {
            id: null,
            received_at: new Date().toISOString(),
            email_send_status: "gated",
        };
    }
    if (resp.status === 429) {
        let retryAfter = parseInt(
            resp.headers.get("Retry-After") || "30",
            10
        );
        if (!Number.isFinite(retryAfter) || retryAfter < 0) retryAfter = 30;
        throw new SubmitError("rate_limit", "Rate limited", { retryAfter });
    }
    if (resp.status === 422) {
        let details = null;
        try {
            details = await resp.json();
        } catch (e) {
            /* silent */
        }
        throw new SubmitError("validation", "Validation failed", {
            validationDetails: details,
        });
    }
    if (resp.status === 503) {
        throw new SubmitError("unavailable", "Service unavailable");
    }
    throw new SubmitError(
        "network",
        `Unexpected response: HTTP ${resp.status}`
    );
}

export { buildPayload };
